import { useState, useEffect, useCallback, useMemo } from 'react';
import { addVote, decreaseVote, InsufficientPointsError } from '../services/voteService';
import { subscribeVotes } from './useVotes';

export function useRankingLogic(persons) {
    const [votes, setVotes] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [voteError, setVoteError] = useState(null);

    const handleVote = useCallback(async (id, increment) => {
        setVoteError(null);
        try {
            if (increment > 0) await addVote(id, increment);
            else await decreaseVote(id, increment);
        } catch (error) {
            if (error instanceof InsufficientPointsError || error.code === 'INSUFFICIENT_POINTS') {
                setVoteError({ type: 'INSUFFICIENT_POINTS', message: error.message });
            } else {
                console.error('Vote error:', error);
            }
        }
    }, []);

    const clearVoteError = useCallback(() => {
        setVoteError(null);
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeVotes((voteData) => {
            setVotes(voteData);
        });
        return () => unsubscribe();
    }, []);

    // Sort persons by vote count immediately (no debounce)
    const sortedPersons = useMemo(() => {
        return persons
            .slice()
            .sort((a, b) => (votes[b.id]?.votes || 0) - (votes[a.id]?.votes || 0));
    }, [votes, persons]);

    const filteredPersons = useMemo(() => {
        return sortedPersons.filter(person => {
            const matchesSearch = person.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || person.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [sortedPersons, searchTerm, selectedCategory]);

    return {
        filteredPersons,
        votes,
        handleVote,
        searchTerm,
        setSearchTerm,
        selectedCategory,
        setSelectedCategory,
        voteError,
        clearVoteError
    };
}
